import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltip } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { DocumentTemplateService } from '../../services/document-template.service';
import { TemplateTransferService } from '../../services/template-transfer.service';
import { DocumentTemplateWithPath } from '../../types/document-template';
import { ResizableContainerComponent } from '../../components/resizable-container/resizable-container.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';
import { Field } from '../../types/fields';
import { FieldCardComponent } from '../fields/components/field-card.component';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { getTemplateAnchorDiff, getTemplateAnchorStatus, isTemplateOutOfSync } from '../../utils/templates.utils';
import { getExpectedFieldIdsForLeaf } from '../../utils/anchor-tree.utils';
import { TemplateAssignmentService } from '../../services/template-assignment.service';
import { DocumentViewerComponent } from '../../components/document-viewer/document-viewer.component';

@Component({
  selector: 'app-template-detail',
  imports: [CommonModule, RouterModule, FormsModule, ResizableContainerComponent, MatIconModule, MatMenuModule, MatDividerModule, MatTooltip, FieldCardComponent, DocumentViewerComponent],
  templateUrl: './template-detail.component.html',
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateDetailComponent implements OnInit {
  route = inject(ActivatedRoute);
  private router = inject(Router);
  private documentTemplateService = inject(DocumentTemplateService);
  private templateTransferService = inject(TemplateTransferService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private title = inject(Title);
  readonly store = inject(OcrPrimeStore);
  private assignmentService = inject(TemplateAssignmentService);

  template = signal<DocumentTemplateWithPath | null>(null);
  isLoading = signal(true);
  isDeleting = signal(false);
  isReextracting = signal(false);
  error = signal<string | null>(null);
  fieldFilter = signal('');

  /** Structure of the template, reactive to the store so late-loading structures resolve. */
  private structure = computed(() => {
    const id = this.template()?.fieldsStructureId;
    return id ? this.store.getStructureById(id) : undefined;
  });

  structureName = computed(() => this.structure()?.name ?? null);

  /** Field definitions of the template's structure, keyed by field id. */
  fieldsMap = computed<{ [fieldId: string]: Field }>(() => {
    const id = this.template()?.fieldsStructureId;
    if (!id) return {};
    const map: { [fieldId: string]: Field } = {};
    for (const field of this.store.getFieldsForStructure(id)) {
      map[field.id] = field;
    }
    return map;
  });

  allFields = computed(() => this.store.fields());

  /** Only meaningful once structures have loaded — avoids a false "out of sync" flash. */
  isOutOfSync = computed(() => {
    const template = this.template();
    return !!template && this.store.structuresLoaded() && isTemplateOutOfSync(template, this.structure());
  });

  /** Tree-scoped anchor status (expected set = path decisive ∪ leaf anchors). */
  anchorStatus = computed(() => {
    const template = this.template();
    if (!template || !this.store.fieldsLoaded() || !this.store.anchorTreeLoaded()) {
      return 'ok' as const;
    }
    return getTemplateAnchorStatus(template, this.store.anchorTreeRoot(), this.store.fieldsByIdMap());
  });

  isAnchorMismatch = computed(() => this.anchorStatus() === 'anchor-mismatch');
  isUnplaced = computed(() => this.anchorStatus() === 'unplaced');

  /** Two-sided diff of the template's anchors against its leaf's expected set. */
  private anchorDiff = computed<{ missing: string[]; extra: string[] }>(() => {
    const template = this.template();
    if (!template) return { missing: [], extra: [] };
    return getTemplateAnchorDiff(template, this.store.anchorTreeRoot(), this.store.fieldsByIdMap());
  });

  /** Names of expected anchors (path ∪ leaf) the template has no OCR value for. */
  missingAnchorNames = computed<string[]>(() => this.anchorDiff().missing);

  /** Names of anchors stored on the template that its leaf does not expect. */
  extraAnchorNames = computed<string[]>(() => this.anchorDiff().extra);

  /** Field names of the path decisive anchors for the template's leaf (UI only — not scored). */
  private pathAnchorNames = computed<Set<string>>(() => {
    const template = this.template();
    const root = this.store.anchorTreeRoot();
    if (!template?.leafNodeId || !root) return new Set();
    const leaf = this.store.leafById(template.leafNodeId);
    if (!leaf) return new Set();
    const fields = this.store.fieldsByIdMap();
    const expected = getExpectedFieldIdsForLeaf(root, template.leafNodeId);
    const leafSet = new Set(leaf.leafAnchorFieldIds ?? []);
    return new Set(
      expected
        .filter(id => !leafSet.has(id))
        .map(id => fields.get(id)?.name)
        .filter((n): n is string => !!n)
    );
  });

  structureKeys = computed(() => Object.keys(this.template()?.structure || {}));

  /** Structure keys narrowed by the field filter (matches field name or key). */
  filteredStructureKeys = computed(() => {
    const filter = this.fieldFilter().trim().toLowerCase();
    if (!filter) return this.structureKeys();
    return this.structureKeys().filter(key => {
      const name = this.fieldsMap()[key]?.name ?? key;
      return name.toLowerCase().includes(filter) || key.toLowerCase().includes(filter);
    });
  });

  /**
   * Anchor rows for display: every value stored on the template, plus a "missing"
   * row for each expected anchor without a value. Path anchors are flagged so
   * the UI can note they're not scored, `extra` rows are values the leaf no
   * longer expects.
   */
  anchorRows = computed<{ key: string; value: string; missing: boolean; extra: boolean; isPath: boolean }[]>(() => {
    const anchors = this.template()?.anchors ?? {};
    const missing = new Set(this.missingAnchorNames());
    const extra = new Set(this.extraAnchorNames());
    const pathNames = this.pathAnchorNames();
    const rows = Object.entries(anchors).map(([key, value]) => ({
      key,
      value: this.formatAnchorValue(value),
      missing: missing.has(key),
      extra: extra.has(key),
      isPath: pathNames.has(key),
    }));
    for (const name of missing) {
      if (!(name in anchors)) {
        rows.push({ key: name, value: '—', missing: true, extra: false, isPath: pathNames.has(name) });
      }
    }
    return rows;
  });

  /** Launches the read-only re-extraction review flow for the template's current leaf. */
  async reextractAnchors(): Promise<void> {
    const template = this.template();
    if (!template) return;
    this.isReextracting.set(true);
    try {
      const saved = await this.assignmentService.reextractForCurrentLeaf(template);
      if (saved) {
        this.loadTemplateDetails(template.id);
      }
    } finally {
      this.isReextracting.set(false);
    }
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadTemplateDetails(id);
    } else {
      this.error.set('Template ID not found in route.');
      this.isLoading.set(false);
    }
  }

  loadTemplateDetails(id: string): void {
    this.isLoading.set(true);
    this.error.set(null);
    // Ensure store has templates, fields, structures, and the anchor tree loaded
    this.store.loadTemplates();
    this.store.loadFields();
    this.store.loadStructures();
    this.store.loadAnchorTree();
    this.documentTemplateService.getDocumentTemplateById(id).subscribe({
      next: (template) => {
        this.template.set(template);
        this.title.setTitle(`${template.name} · Templates`);
        // A missing file only disables the preview pane — the rest of the page still renders.
        if (!template.fileUrl) {
          console.warn('Template fetched but fileUrl is missing:', template);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading template details:', err);
        this.error.set('Failed to load template details. Please try again later.');
        this.isLoading.set(false);
      }
    });
  }

  async exportTemplate(): Promise<void> {
    const template = this.template();
    if (!template) return;
    try {
      await this.templateTransferService.exportTemplate(template);
      this.snackBar.open(`Template "${template.name}" copied to clipboard`, 'Close', { duration: 3000 });
    } catch (err) {
      console.error('Error exporting template:', err);
      this.snackBar.open('Failed to copy template to clipboard.', 'Close', { duration: 4000 });
    }
  }

  toggleBlocked(): void {
    const template = this.template();
    if (!template) return;
    const newAutoBlocked = !template.autoBlocked;
    const payload: Partial<DocumentTemplateWithPath> = {
      name: template.name,
      description: template.description,
      structure: template.structure,
      fieldsStructureId: template.fieldsStructureId,
      autoBlocked: newAutoBlocked,
    };
    this.documentTemplateService.updateDocumentTemplate(template.id, payload).subscribe({
      next: () => {
        this.template.update(t => t ? { ...t, autoBlocked: newAutoBlocked } : t);
        this.snackBar.open(
          newAutoBlocked
            ? `Template "${template.name}" blocked — associated documents will go to manual review`
            : `Template "${template.name}" unblocked`,
          'Close',
          { duration: 4000 }
        );
      },
      error: (err) => {
        console.error('Error toggling template blocked state:', err);
        this.snackBar.open('Failed to update the template blocked state. Please try again later.', 'Close', { duration: 4000 });
      }
    });
  }

  deleteTemplate(): void {
    const template = this.template();
    if (!template) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Delete template "${template.name}"?`,
        message: 'This action cannot be undone.',
        consequences: ['Documents matched to this template will no longer be processed with it.'],
        confirmLabel: 'Delete Template'
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isDeleting.set(true);
      this.documentTemplateService.deleteDocumentTemplate(template.id).subscribe({
        next: () => {
          // The backend also strips the template from its anchor-tree leaf.
          this.store.refreshAnchorTree();
          this.snackBar.open(`Template "${template.name}" deleted`, 'Close', { duration: 3000 });
          this.router.navigate(['/templates']);
        },
        error: (err) => {
          console.error('Error deleting document template:', err);
          this.isDeleting.set(false);
          this.snackBar.open('Failed to delete the template. Please try again later.', 'Close', { duration: 4000 });
        }
      });
    });
  }

  // Helper to format dates
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  /** Compact relative time for the header meta strip ("3 days ago"); exact date on tooltip. */
  relativeTime(dateString: string | undefined): string {
    if (!dateString) return '';
    const diffMs = Date.now() - new Date(dateString).getTime();
    if (isNaN(diffMs) || diffMs < 0) return this.formatDate(dateString);
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }

  private formatAnchorValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
}
